import { useState, useEffect } from "react";
import { Navigate, useParams, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import Navigation from "./components/Navigation";
import { translations } from "./locales/translations";
import GlobalStyle from "./styles/GlobalStyle";
import City from "./components/3D/City";
import {
  isNavTab,
  isSupportedLang,
  NAV_TABS,
} from "./constants/i18nRoutes";

const AppContainer = styled.div`
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
`;

const SafeAreaWrapper = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  padding: max(env(safe-area-inset-bottom), 1rem) env(safe-area-inset-right);
  pointer-events: none;
  min-height: 120px;
  background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.3));
  z-index: 99;
`;

const ControlsContainer = styled.div`
  position: absolute;
  bottom: max(2rem, env(safe-area-inset-bottom));
  right: max(1rem, env(safe-area-inset-right));
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1rem;
  z-index: 100;
  padding: 1rem;
  pointer-events: auto;
  width: fit-content;
  min-width: 150px;

  @media (max-height: 667px) {
    bottom: max(1.5rem, env(safe-area-inset-bottom));
    gap: 0.75rem;
  }
`;

const DizzyButton = styled.button`
  padding: 0.75rem 1.25rem;
  background-color: rgba(83, 83, 83, 0.8);
  color: white;
  border-radius: 8px;
  font-size: clamp(0.875rem, 2.5vw, 1rem);
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
  backdrop-filter: blur(4px);
  width: 100%;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: rgba(54, 54, 54, 0.9);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const CopyrightText = styled.div`
  color: white;
  font-size: clamp(0.7rem, 2vw, 0.8rem);
  opacity: 0.9;
  font-weight: 400;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  width: 100%;
  text-align: right;
  padding: 0.25rem 0;
`;

const CanvasWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

export default function SiteShell() {
  const { lang, tab } = useParams();
  const location = useLocation();
  const [rotationEnabled, setRotationEnabled] = useState(true);
  const [isTabOpen, setIsTabOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang ?? "");
  }, [lang]);

  useEffect(() => {
    if (!lang || !isSupportedLang(lang)) {
      return;
    }
    try {
      localStorage.setItem("language", lang);
    } catch (e) {
      console.warn("localStorage language sync failed:", e?.message);
    }
  }, [lang]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1650);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!isSupportedLang(lang)) {
    if (isNavTab(lang)) {
      return <Navigate to={`/en/${lang}`} replace />;
    }
    return <Navigate to="/en" replace />;
  }

  if (tab != null && tab !== "" && !NAV_TABS.includes(tab)) {
    return <Navigate to={`/${lang}`} replace />;
  }

  const activeTab = tab && NAV_TABS.includes(tab) ? tab : null;

  return (
    <>
      <GlobalStyle />
      <AppContainer>
        <Navigation
          key={location.pathname}
          lang={lang}
          activeTab={activeTab}
          onActiveTabChange={(open) => setIsTabOpen(!!open)}
        />
        <CanvasWrapper>
          <Canvas camera={{ position: [0, 2, 20], fov: 40 }}>
            <OrbitControls
              enableZoom={true}
              enablePan={true}
              enableRotate={true}
              minDistance={10}
              maxDistance={50}
              enableDamping={true}
              dampingFactor={0.05}
            />
            <fog attach="fog" args={["#E2E8F0", 0, 500]} />
            <ambientLight color="#FFFFFF" intensity={0.5} />
            <City rotationEnabled={rotationEnabled} />
          </Canvas>
        </CanvasWrapper>
        <SafeAreaWrapper>
          <ControlsContainer>
            {!(isMobile && isTabOpen) && (
              <>
                <DizzyButton
                  onClick={() => setRotationEnabled((prev) => !prev)}
                >
                  {
                    translations[lang][
                      rotationEnabled ? "imDizzy" : "feelingBetter"
                    ]
                  }
                </DizzyButton>
                <CopyrightText>{translations[lang].copyright}</CopyrightText>
              </>
            )}
          </ControlsContainer>
        </SafeAreaWrapper>
      </AppContainer>
    </>
  );
}
